// Layout principal — substitui sidebar 260px por Topbar + Sidebar 48px + Canvas full + EditPanel + ContextButton.
// Onboarding (sem workspace) vira modal sobre canvas vazio (não tela cheia).

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useDocuments, useLinks, useMe, useWorkspaces } from '../lib/queries.js';
import { useStore } from '../store/useStore.js';
import { GraphCanvas } from '../canvas/GraphCanvas.js';
import { useGraphRealtime } from '../realtime/useGraphRealtime.js';
import { useDirtySync } from '../realtime/useDirtySync.js';
import { Topbar } from './Topbar.js';
import { Sidebar } from './Sidebar.js';
import { ContextButton } from './ContextButton.js';
import { EditPanel } from './EditPanel.js';
import { ListView } from './ListView.js';
import { ShortcutsModal } from './ShortcutsModal.js';
import { CreateWorkspaceModal } from './CreateWorkspaceModal.js';
import { FloatingActionBar } from './FloatingActionBar.js';
import { TimelineScrubber } from './TimelineScrubber.js';
import { UniverseMap } from './UniverseMap.js';
import { CanvasOnboarding } from './CanvasOnboarding.js';
import { CanvasContextMenu } from './CanvasContextMenu.js';
import { MultiSelectBar } from './MultiSelectBar.js';
import { CommandPalette } from './CommandPalette.js';
import { TemplatesModal } from './TemplatesModal.js';
import { NodePreviewTooltip } from './NodePreviewTooltip.js';
import { InlineLabelEditor } from './InlineLabelEditor.js';
import { InlineNoteEditor } from './InlineNoteEditor.js';
import { Minimap } from './Minimap.js';
import { AIChatDrawer } from './AIChatDrawer.js';
import { AssistantHUD } from './AssistantHUD.js';
import { FocusAudioMenu } from './FocusAudioMenu.js';


interface Props {
  children: ReactNode;
}

export function WorkspaceShell({ children }: Props) {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { data: workspaces } = useWorkspaces();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const ws = workspaces?.workspaces.find((w) => w.slug === slug);

  const docs = useDocuments(ws?.id, null, true);
  const links = useLinks(ws?.id);
  const realtime = useGraphRealtime(ws?.id);
  useDirtySync(ws?.id);

  const setAuth = useStore((s) => s.setAuth);
  const setWorkspaceId = useStore((s) => s.setWorkspaceId);
  const viewMode = useStore((s) => s.viewMode);
  const undo = useStore((s) => s.undo);
  const redo = useStore((s) => s.redo);
  const deleteNode = useStore((s) => s.deleteNode);
  const setSelected = useStore((s) => s.setSelected);
  const setMultiSelected = useStore((s) => s.setMultiSelected);
  const addNode = useStore((s) => s.addNode);
  const zoomToFit = useStore((s) => s.zoomToFit);
  const setCmdPaletteOpen = useStore((s) => s.setCmdPaletteOpen);
  const toggleSnapToGrid = useStore((s) => s.toggleSnapToGrid);
  const setEditingLabelId = useStore((s) => s.setEditingLabelId);
  const editPanelOpen = useStore((s) => s.editPanelOpen);


  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showCreateWs, setShowCreateWs] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // Marca booted depois de hidratar.
  useEffect(() => {
    useStore.setState({ booted: true });
  }, []);

  // Auth → store.
  useEffect(() => {
    if (me) setAuth({ id: me.id, email: me.email, name: me.name, isAdmin: me.admin !== null });
  }, [me, setAuth]);

  // Workspace ID → store (needed for direct REST persistence).
  useEffect(() => {
    setWorkspaceId(ws?.id ?? null);
    return () => setWorkspaceId(null);
  }, [ws?.id, setWorkspaceId]);

  // Clear nodes/links when workspace changes so stale data never shows.
  useEffect(() => {
    if (!ws?.id) return;
    const st = useStore.getState();
    st.setNodes([]);
    st.setLinks([]);
    st.setSelected(null);
  }, [ws?.id]);

  // Repara Yjs com dados do REST: adiciona apenas itens ausentes do Yjs.
  // Preserva dados colaborativos existentes; restaura itens perdidos por race
  // condition (provider.destroy() antes do WebSocket sincronizar com Hocuspocus).
  // REST is the authoritative source. Populate store directly on load/refresh.
  // Also broadcast to Yjs so remote collab peers receive the initial state.
  const extractFrontendText = (content: any): string => {
    if (!content) return '';
    if (typeof content === 'string') return content;
    if (typeof content === 'object') {
      const out: string[] = [];
      const walk = (n: any) => {
        if (!n || typeof n !== 'object') return;
        if (typeof n.text === 'string') out.push(n.text);
        if (Array.isArray(n.content)) n.content.forEach(walk);
      };
      walk(content);
      return out.join(' ');
    }
    return '';
  };

  useEffect(() => {
    if (!docs.data) return;
    const incoming = docs.data.documents.map((d) => ({
      id: d.id,
      x: d.position?.x ?? Math.random() * 600 - 300,
      y: d.position?.y ?? Math.random() * 400 - 200,
      vx: 0,
      vy: 0,
      size: d.position?.size ?? 22,
      locked: d.position?.locked ?? false,
      parentId: d.parentId ?? null,
      label: d.title,
      color: d.color ?? '#00f5ff',
      category: d.category ?? 'idea',
      type: d.type ?? 'idea',
      icon: d.icon ?? undefined,
      content: extractFrontendText(d.content),
    }));
    // Merge: keep local-only nodes (pending persist) so optimistic Flow creates don't
    // disappear when REST refetches before persistNode completes.
    const incomingIds = new Set(incoming.map((n) => n.id));
    const pending = useStore.getState().nodes.filter((n) => !incomingIds.has(n.id));
    useStore.getState().setNodes([...incoming, ...pending]);
    const handle = realtime.handle;
    if (handle) {
      for (const d of docs.data.documents) {
        if (handle.ynodes.has(d.id)) continue;
        handle.upsertNode({
          id: d.id,
          x: d.position?.x ?? Math.random() * 600 - 300,
          y: d.position?.y ?? Math.random() * 400 - 200,
          size: d.position?.size ?? 22,
          locked: d.position?.locked ?? false,
          label: d.title,
          color: d.color ?? '#00f5ff',
          category: d.category,
          type: d.type,
          parentId: d.parentId,
          icon: d.icon ?? undefined,
          content: extractFrontendText(d.content),
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs.data]);

  useEffect(() => {
    if (!links.data) return;
    const incoming = links.data.links.map((l) => ({
      id: l.id,
      source: l.sourceId,
      target: l.targetId,
      type: l.type,
      strength: l.strength,
    }));
    const incomingIds = new Set(incoming.map((l) => l.id));
    const pending = useStore.getState().links.filter((l) => !incomingIds.has(l.id));
    useStore.getState().setLinks([...incoming, ...pending]);
    const handle = realtime.handle;
    if (handle) {
      for (const l of links.data.links) {
        if (handle.ylinks.has(l.id)) continue;
        handle.upsertLink({
          id: l.id,
          source: l.sourceId,
          target: l.targetId,
          type: l.type,
          strength: l.strength,
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links.data]);

  // Atalhos globais.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const inEditor =
        ['INPUT', 'TEXTAREA'].includes((document.activeElement as HTMLElement | null)?.tagName ?? '') ||
        (document.activeElement as HTMLElement | null)?.isContentEditable;
      if (inEditor) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
        e.preventDefault(); redo(); return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const st = useStore.getState();
        if (st.selectedId) { e.preventDefault(); deleteNode(st.selectedId); return; }
        if (st.multiSelected.size > 0) {
          e.preventDefault();
          [...st.multiSelected].forEach((id) => deleteNode(id));
          setMultiSelected(new Set());
          return;
        }
      }
      if (e.key === 'Escape') {
        setSelected(null);
        setMultiSelected(new Set());
        useStore.getState().setLinkingFrom(null);
      }
      if (e.key === '?') { e.preventDefault(); setShowShortcuts((s) => !s); }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdPaletteOpen(true); return; }
      if (e.key === 'f' || e.key === 'F') { e.preventDefault(); zoomToFit(); return; }
      if (e.altKey && (e.key === 'g' || e.key === 'G')) { e.preventDefault(); toggleSnapToGrid(); return; }
      if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
        const st = useStore.getState();
        if (st.selectedId) { e.preventDefault(); setEditingLabelId(st.selectedId); return; }
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        const st = useStore.getState();
        const nodes = st.nodes;
        if (!nodes.length) return;
        const idx = nodes.findIndex((n) => n.id === st.selectedId);
        const next = e.shiftKey
          ? (idx <= 0 ? nodes.length - 1 : idx - 1)
          : (idx >= nodes.length - 1 ? 0 : idx + 1);
        setSelected(nodes[next]!.id);
        return;
      }
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        const state = useStore.getState();
        const cam = state.cam;
        const id = addNode({
          label: 'Nova Ideia', type: 'idea', category: 'idea', icon: 'lightbulb',
          x: -cam.x + Math.random() * 200 - 100,
          y: -cam.y + Math.random() * 200 - 100,
          size: 22,
        });
        setTimeout(() => setSelected(id), 10);
        return;
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [undo, redo, deleteNode, setSelected, setMultiSelected, addNode, zoomToFit, setCmdPaletteOpen, toggleSnapToGrid, setEditingLabelId]);

  // Sem workspace → mostra Onboarding.
  const noWorkspace = !!workspaces && workspaces.workspaces.length === 0;
  // Workspace na URL não existe.
  const wsNotFound = !!workspaces && workspaces.workspaces.length > 0 && !ws;

  if (noWorkspace) {
    return (
      <>
        <EmptyHero
          title="Bem-vindo ao Flowmindy"
          subtitle="Você ainda não tem nenhum workspace. Crie o primeiro para começar."
          actionLabel="Criar workspace"
          onAction={() => setShowCreateWs(true)}
        />
        <CreateWorkspaceModal open={showCreateWs} onClose={() => setShowCreateWs(false)} />
      </>
    );
  }

  if (wsNotFound) {
    return (
      <EmptyHero
        title="Workspace não encontrado"
        subtitle={`/${slug} não existe ou você não tem acesso.`}
        actionLabel="Ver meus workspaces"
        onAction={() => navigate({ to: '/' })}
      />
    );
  }

  if (!ws) return <div style={{ padding: 32, color: 'var(--muted)' }}>carregando…</div>;

  async function handleExportPng() {
    const state = useStore.getState();
    state.zoomToFit();
    // Wait 2 frames for renderer to apply new camera.
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
    const canvas = document.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `flowmindy-${new Date().toISOString().slice(0, 10)}.png`;
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handleExport() {
    const st = useStore.getState();
    const data = JSON.stringify({ nodes: st.nodes, links: st.links, focusedParentId: st.focusedParentId, exportedAt: Date.now() }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `flowmindy-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string);
        if (data.nodes && data.links) {
          useStore.getState().snapshot();
          useStore.getState().applySnapshot({ nodes: data.nodes, links: data.links, focusedParentId: data.focusedParentId ?? null });
        }
      } catch (err) { console.error('Import failed:', err); }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function handleAddNode() {
    const state = useStore.getState();
    const cam = state.cam;
    const id = addNode({
      label: 'Nova Ideia', type: 'idea', category: 'idea', icon: 'lightbulb',
      x: -cam.x + Math.random() * 200 - 100,
      y: -cam.y + Math.random() * 200 - 100,
      size: 22,
    });
    setTimeout(() => setSelected(id), 10);
  }

  return (
    <>
      <Topbar
        workspace={ws}
        onExport={handleExport}
        onExportPng={handleExportPng}
        onImport={handleImport}
        onOpenCreateWs={() => setShowCreateWs(true)}
        onOpenShortcuts={() => setShowShortcuts(true)}
      />
      {viewMode === 'graph' && <Sidebar />}
      {viewMode === 'graph' && <UniverseMap />}
      {viewMode === 'graph'
        ? <GraphCanvas onWorldMove={(x, y) => realtime.publishCursor(x, y)} />
        : <ListView />
      }
      {viewMode === 'graph' && editPanelOpen && <EditPanel />}
      {viewMode === 'graph' && <CanvasOnboarding onAdd={handleAddNode} />}
      {viewMode === 'graph' && <MultiSelectBar />}
      {viewMode === 'graph' && <TimelineScrubber />}
      {viewMode === 'graph' && <FloatingActionBar onOpenShortcuts={() => setShowShortcuts(true)} onOpenTemplates={() => setShowTemplates(true)} />}
      <InlineLabelEditor />
      {viewMode === 'graph' && <InlineNoteEditor />}
      {viewMode === 'graph' && <Minimap />}
      {viewMode === 'graph' && <NodePreviewTooltip />}
      <CanvasContextMenu />
      <CommandPalette />
      <AIChatDrawer />
      <AssistantHUD />
      <FocusAudioMenu />
      <TemplatesModal open={showTemplates} onClose={() => setShowTemplates(false)} />
      <ShortcutsModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
      <CreateWorkspaceModal open={showCreateWs} onClose={() => setShowCreateWs(false)} />

      {children}
    </>
  );
}

function EmptyHero({ title, subtitle, actionLabel, onAction }: { title: string; subtitle: string; actionLabel: string; onAction: () => void }) {
  return (
    <main style={{
      position: 'fixed', inset: 0,
      background: 'var(--canvas-bg)',
      display: 'grid', placeItems: 'center', padding: 24,
    }}>
      <div style={{ textAlign: 'center', maxWidth: 460 }}>
        <div style={{
          fontSize: 32, fontWeight: 700, marginBottom: 12,
          background: 'linear-gradient(135deg, var(--neon), var(--violet))',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {title}
        </div>
        <p style={{ color: 'var(--muted)', marginBottom: 24, fontSize: 14 }}>{subtitle}</p>
        <button
          onClick={onAction}
          style={{
            background: 'var(--neon)', color: '#0a0a0f', border: 'none',
            borderRadius: 10, padding: '12px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
            boxShadow: '0 0 20px rgba(0,245,255,0.4)',
          }}
        >
          {actionLabel}
        </button>
      </div>
    </main>
  );
}
